import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatExactMinorUnits } from '@norvix/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';

import { customerActionsApi } from '../../api/customer-actions.api.js';
import {
  customersApi,
  type PiiFieldType,
} from '../../api/customers.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { CommentsPanel } from '../../components/CommentsPanel.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { ViewAsCustomerButton } from '../../components/ViewAsCustomerButton';

import { AccountStatementsDialog } from './AccountStatementsDialog.js';
import { ApproveKycButton } from './ApproveKycButton.js';
import { CustomerAccountsPanel } from './CustomerAccountsPanel.js';
import { CustomerActivityTab } from './CustomerActivityTab';
import { CustomerCaseFileTab } from './CustomerCaseFileTab.js';
import { CustomerFeesTab } from './CustomerFeesTab';
import { CorrectLegalNameButton } from './CorrectLegalNameButton.js';
import { GurupayIndustryTypeField } from './GurupayIndustryTypeField.js';
import { IbanqSubAccountStatus } from './IbanqSubAccountStatus.js';
import { CustomerKycTab } from './CustomerKycTab.js';
import { CustomerPaymentsTab } from './CustomerPaymentsTab';
import { BreadcrumbLabel } from '../../layout/breadcrumb-labels.js';
import { CompanyScreeningPanel } from '../../components/CompanyScreeningPanel.js';
import { CustomerIdentifier,IdentityField, WorkspaceSectionHeader, customerWorkspaceSx } from './CustomerWorkspace.js';
import { SetRiskRatingButton } from './SetRiskRatingButton.js';
import { openAccountCurrencies, providersFor } from './open-account-options.js';
import { DeclaredInformationPanel } from '../../components/DeclaredInformationPanel.js';
import { DuplicatePersonAttachPanel } from '../../components/DuplicatePersonAttachPanel.js';
import { RiskScoringPanel } from '../../components/RiskScoringPanel.js';
import { APP_LOCALE } from '../../utils/locale.js';


type RevealState = Partial<Record<PiiFieldType, string>>;

const PROVIDER_COMPANY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatCents(cents: string, currency: string): string {
  try {
    return `${formatExactMinorUnits(BigInt(cents), currency, true)} ${currency}`;
  } catch {
    return `${cents} ${currency}`;
  }
}

/**
 * A table with no rows must say so. Rendering a bare header with an empty body
 * reads as a broken page — the operator cannot tell "nothing here" from
 * "failed to load".
 */
function EmptyRow({ colSpan, message }: { colSpan: number; message: string }): JSX.Element {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
        >
          {message}
        </Typography>
      </TableCell>
    </TableRow>
  );
}


const CUSTOMER_TABS = [
  ['Profile', 'Customer identity and internal comments.'],
  ['KYC', 'Verification, recorded reports and documents on file.'],
  ['Accounts', 'Balances, account details and statements.'],
  ['Cards', 'Cards linked to this customer.'],
  ['Transactions', 'Payments and posted fees, newest first.'],
  ['Alerts', 'Recorded compliance alerts and screening status.'],
  ['Consents', 'Permissions granted or revoked by this customer.'],
  ['Provider', 'Linked provider records and verification.'],
  ['Fees', 'Pricing and fee collection settings for this customer.'],
  ['Activity', 'Recorded activity on this customer’s account.'],
  ['Case file', 'Customer review history and evidence.'],
] as const;

/** The case file is KYC data (DEV-67): its tab is shown to admin and AML / MLRO only. */
const CASE_FILE_TAB = CUSTOMER_TABS.findIndex(([label]) => label === 'Case file');

export function Customer360Page(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const customerId = id ?? '';
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [otherTab, setOtherTab] = useState(0);
  const tab = searchParams.get('tab') === 'kyc' ? 1 : otherTab;
  const setTab = (value: number): void => {
    setOtherTab(value === 1 ? 0 : value);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value === 1) next.set('tab', 'kyc');
      else next.delete('tab');
      return next;
    }, { replace: true });
  };
  const [revealed, setRevealed] = useState<RevealState>({});
  const [statementAccountId, setStatementAccountId] = useState<string | null>(null);

  const {
    data: customer,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async () => customersApi.getCustomer(customerId),
    enabled: customerId !== '',
  });

  // Parties on a business application — the people who must each pass an
  // identity check, since the provider has no company-level one.
  const {
    data: partiesData,
    isError: partiesFailed,
    refetch: refetchParties,
  } = useQuery({
    queryKey: ['verification-parties', customerId],
    queryFn: async () => customersApi.verificationParties(customerId),
    enabled: customerId !== '',
    retry: false,
  });

  const [issuedLinks, setIssuedLinks] = useState<Record<string, string>>({});
  // A submission can miss the provider entirely — sandbox credentials, an
  // outage, a console configured but not completed. The application still lands
  // here, but with no company or person profile there is nothing to screen and
  // nothing to issue a link against. This pushes it to the provider configured
  // now, which was previously impossible: the payload only existed in the
  // applicant's browser.
  const [pushState, setPushState] = useState<'idle' | 'busy' | 'done' | 'partial' | 'error'>(
    'idle',
  );
  const [pushMessage, setPushMessage] = useState<string>('');
  const [providerCompanyId, setProviderCompanyId] = useState('');

  const pushToProvider = async (existingCompanyId?: string): Promise<void> => {
    setPushState('busy');
    setPushMessage('');
    try {
      const result = await customersApi.createProviderProfile(
        customerId,
        existingCompanyId === undefined ? undefined : { companyProviderId: existingCompanyId },
      );
      const verb = result.attachedToExisting ? 'completed' : 'created';
      const reused =
        result.personsReused > 0
          ? ` ${String(result.personsReused)} existing profile(s) reused — no duplicates created.`
          : '';
      if (result.detailSynced) {
        setPushState('done');
        setPushMessage(
          `Company ${verb} at the provider (${result.providerId}): ` +
            `${String(result.directorsAdded)} director(s), ` +
            `${String(result.beneficialOwnersAdded)} owner(s). Company details synced.${reused}`,
        );
      } else {
        // The company exists, but the write carrying addresses/contacts was
        // refused — say so, or the vendor console shows an empty profile with
        // no explanation.
        setPushState('partial');
        setPushMessage(
          `Company ${verb} at the provider (${result.providerId}), but the company ` +
            `details did NOT sync: ${result.detailSyncError ?? 'unknown reason'}.${reused}`,
        );
      }
      setProviderCompanyId('');
      await refetchParties();
    } catch (error: unknown) {
      setPushState('error');
      setPushMessage(error instanceof Error ? error.message : 'Could not reach the provider.');
      // A refused duplicate person is kept with its candidates (DEV-126).
      void refetchParties();
    }
  };
  const needsCompanyRelink =
    pushState === 'error' && /already exists|POST \/companies failed with 501/i.test(pushMessage);

  // Cleanup for a duplicate related person — the attach path used to create a
  // new person per press, and those extra rows need a way out. Kept behind an
  // explicit toggle: it deletes the person at the provider, taking any passed
  // identity check with them.
  // Opening an account by hand: for customers approved before approval did it
  // itself, and for retrying an opening that failed.
  const [openAccountBusy, setOpenAccountBusy] = useState(false);
  const [openAccountReason, setOpenAccountReason] = useState('');
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [openAccountError, setOpenAccountError] = useState('');
  const [openAccountOutcome, setOpenAccountOutcome] = useState('');
  const [openAccountCurrency, setOpenAccountCurrency] = useState('EUR');
  const [openAccountType, setOpenAccountType] = useState('CURRENT');
  // Empty until the API says what can open an account. Hardcoding a default
   // here is how the dialog kept preselecting the in-house ledger after it
   // stopped being offered.
  const [openAccountProvider, setOpenAccountProvider] = useState('');
  // GuruPay's industry type, asked when a business is opened at GuruPay with
  // none on file; starts from the stored one, which is not sent again.
  const [openAccountIndustry, setOpenAccountIndustry] = useState<number | null>(null);
  // IBANQ's GICS sub-industry code, asked when a business is opened at IBANQ
  // with none on file (DEV-149); kept as typed until it is 8 digits.
  const [openAccountGics, setOpenAccountGics] = useState('');

  // The provider list comes from the API, never from a constant in this bundle:
  // a provider that is not connected must not be offerable by a stale deploy.
  const providersQuery = useQuery({
    queryKey: ['account-providers'],
    queryFn: () => customersApi.accountProviders(),
    staleTime: 5 * 60 * 1000,
  });
  const providers = providersQuery.data?.providers ?? [];
  const defaultProvider = providersQuery.data?.defaultProvider ?? '';
  if (openAccountProvider === '' && defaultProvider !== '') {
    setOpenAccountProvider(defaultProvider);
  }
  const chosenProvider = providers.find((p) => p.id === openAccountProvider);

  // DEV-162: the currencies come from Settings → Organisation → Currencies
  // offered (with the providers, from the API), each provider listing the ones
  // it can hold today. Only currencies some provider can open are offered;
  // the rest are named with the reason.
  const currencyOptions = openAccountCurrencies(providersQuery.data?.offeredCurrencies ?? [], providers);
  if (currencyOptions.choosable.length > 0 && !currencyOptions.choosable.some((c) => c.code === openAccountCurrency)) {
    setOpenAccountCurrency(currencyOptions.choosable[0]?.code ?? 'EUR');
  }
  const optionsForChosenCurrency = providersFor(providers, openAccountCurrency);

  const openAccount = async (gurupayIndustryType?: number, ibanqGicsCode?: number): Promise<void> => {
    if (openAccountReason.trim().length < 4) return;
    setOpenAccountBusy(true);
    setOpenAccountError('');
    try {
      const result = await customersApi.openAccount(customerId, {
        reason: openAccountReason.trim(),
        currency: openAccountCurrency,
        productType: openAccountType,
        provider: openAccountProvider,
        ...(gurupayIndustryType === undefined ? {} : { gurupayIndustryType }),
        ...(ibanqGicsCode === undefined ? {} : { ibanqGicsCode }),
      });
      setOpenAccountDialog(false);
      setOpenAccountReason('');
      // "Opened" means something different at each partner, so say which.
      setOpenAccountOutcome(result.summary);
      await queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    } catch (error: unknown) {
      setOpenAccountError(
        error instanceof Error ? error.message : 'Could not open the account.',
      );
    } finally {
      setOpenAccountBusy(false);
    }
  };

  const [manageParties, setManageParties] = useState(false);
  const [removeBusyId, setRemoveBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<
    { providerId: string; fullName: string; decision: string } | null
  >(null);
  const [removeReason, setRemoveReason] = useState('');

  const confirmRemoval = async (): Promise<void> => {
    if (removeTarget === null || removeReason.trim().length < 4) return;
    setRemoveBusyId(removeTarget.providerId);
    setLinkError(null);
    try {
      await customersApi.removeParty(customerId, removeTarget.providerId, removeReason.trim());
      setRemoveTarget(null);
      setRemoveReason('');
      await refetchParties();
    } catch (error: unknown) {
      setLinkError(error instanceof Error ? error.message : 'Could not remove the party.');
    } finally {
      setRemoveBusyId(null);
    }
  };
  const [linkError, setLinkError] = useState<string | null>(null);
  const issueLinkMutation = useMutation({
    mutationFn: async (personId: string) => customersApi.issuePartyLink(customerId, personId),
    onSuccess: (result, personId) => {
      setLinkError(null);
      setIssuedLinks((prev) => ({ ...prev, [personId]: result.verificationLink }));
    },
    onError: (err: Error) => {
      setLinkError(err.message);
    },
  });

  /**
   * Close an account.
   *
   * For an account that should not exist — one opened before an operator
   * chose a provider, or opened against the wrong one. The API refuses to
   * close an account still holding money, so this cannot strand a balance.
   */
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const closeAccountMutation = useMutation({
    mutationFn: async (input: { accountId: string; reason: string }) =>
      customerActionsApi.closeAccount(customerId, input.accountId, input.reason),
    onSuccess: async () => {
      setCloseError(null);
      setClosingId(null);
      await queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    },
    onError: (err: Error) => {
      setCloseError(err.message);
      setClosingId(null);
    },
  });

  const revealMutation = useMutation({
    mutationFn: async (fieldType: PiiFieldType) =>
      customersApi.revealPii(customerId, fieldType),
    onSuccess: (result, fieldType) => {
      setRevealed((prev) => ({ ...prev, [fieldType]: result.value }));
    },
  });

  if (customerId === '') {
    return <Alert severity="error">Missing customer ID</Alert>;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error !== null && error !== undefined) {
    return (
      <Alert severity="error">
        Failed to load customer: {(error).message}
      </Alert>
    );
  }

  const data = customer;
  if (data === undefined) {
    return <Alert severity="warning">Customer not found</Alert>;
  }

  const canSeeTransactions = can('customers.view');
  // DEV-67: KYC work (rating, legal name, screening, risk scoring, reports) is
  // admin and AML / MLRO; comments are also the client manager's.
  const canDecideKyc = can('kyc.decide');
  const canComment = can('comments.write');
  const canReadOnboarding = canDecideKyc;
  const isBusiness = data.accountType === 'BUSINESS' || (data.accountType === undefined && data.business !== null);
  const statementAccount = data.accounts.find((account) => account.accountId === statementAccountId);
  const storedIndustry = data.business?.gurupayIndustryType ?? null;
  // GuruPay keeps an industry type on a company's vIBAN; a personal customer is never asked.
  const asksIndustry = isBusiness && openAccountProvider === 'GURUPAY_VIBAN';
  // IBANQ opens a company's sub-account only with its GICS code; asked once.
  const storedGics = data.business?.ibanqGicsCode ?? null;
  const asksGics = isBusiness && openAccountProvider === 'IBANQ_SUBACCOUNT' && storedGics === null;
  const gicsValid = /^\d{8}$/.test(openAccountGics.trim());

  const renderPiiField = (
    label: string,
    maskedValue: string,
    fieldType: PiiFieldType,
  ): JSX.Element => {
    const reveal = revealed[fieldType];
    return (
      <IdentityField label={label} action={reveal === undefined ? (
        <Button size="small" className="workspace-link" disabled={revealMutation.isPending}
          onClick={() => revealMutation.mutate(fieldType)}>Reveal</Button>
      ) : <Chip label="Revealed (audit logged)" color="warning" size="small" />}>
        {reveal ?? maskedValue}
      </IdentityField>
    );
  };

  return (
    <Box sx={customerWorkspaceSx}>
      <BreadcrumbLabel id={data.customerId} label={data.identity.displayName ?? (isBusiness ? data.business?.legalName : undefined)} />
      <Box component="header" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'anywhere' }}>
            <Typography variant="h4" component="h1">
              {revealed.name ?? data.identity.displayName ?? (isBusiness ? data.business?.legalName : undefined) ?? data.identity.maskedEmail}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
              <StatusBadge kind="subject" value={data.accountType ?? (isBusiness ? 'BUSINESS' : 'INDIVIDUAL')} />
              {isBusiness && data.business ? <Typography variant="caption" color="text.secondary">
                Reg. {data.business.registrationNumber} · {data.business.incorporationCountry}
              </Typography> : null}
              <CustomerIdentifier customerId={data.customerId} />
            </Stack>
          </Box>
          {can('impersonate') || can('customer.actions') ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {can('impersonate') ? <ViewAsCustomerButton customerId={data.customerId} /> : null}
              {can('customer.actions') ? <Button component={RouterLink} to={`/customers/${data.customerId}/actions`} variant="contained">Actions</Button> : null}
            </Stack>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 2 }}>
          <StatusBadge kind="kyc" value={data.kyc.status ?? 'UNKNOWN'} />
          <StatusBadge kind="risk" value={data.kyc.riskRating} />
          {data.approval ? (
            <Tooltip title={data.approval.approvedBy ? `Approved by ${data.approval.approvedBy}` : 'Approved at onboarding'}>
              <Chip size="small" variant="outlined" color="success" label={`Approved ${new Date(data.approval.approvedAt).toLocaleDateString(APP_LOCALE)}`} />
            </Tooltip>
          ) : null}
          {(data.documents ?? []).some((d) => d.needsAttention) ? (
            <Tooltip
              title={(data.documents ?? [])
                .filter((d) => d.needsAttention)
                .map((d) => `${d.documentType.replace(/_/g, ' ')}: ${d.validityDetail}`)
                .join(' · ')}
            >
              <Chip size="small" color="warning" label="Documents need attention" />
            </Tooltip>
          ) : null}
          {data.review?.overdue === true ? (
            <Chip
              size="small"
              color="error"
              label={`Review overdue${
                data.review.nextReviewAt !== null
                  ? ` since ${new Date(data.review.nextReviewAt).toLocaleDateString(APP_LOCALE)}`
                  : ''
              }`}
            />
          ) : data.review?.nextReviewAt !== undefined && data.review.nextReviewAt !== null ? (
            <Chip
              size="small"
              variant="outlined"
              label={`Review due ${new Date(data.review.nextReviewAt).toLocaleDateString(APP_LOCALE)}`}
            />
          ) : (
            <Tooltip title="No review has been completed yet, so nothing is scheduled. Record an evaluation on the case file to start the cycle.">
              <Chip size="small" variant="outlined" color="warning" label="No review scheduled" />
            </Tooltip>
          )}
          {data.complianceFlags.hasOpenSar === true ? (
            <StatusBadge kind="aml" value="SAR" />
          ) : null}
        </Stack>
      </Box>
      <Tabs value={tab} onChange={(_, value: number) => setTab(value)} variant="scrollable"
        scrollButtons="auto" allowScrollButtonsMobile aria-label="Customer workspace"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        {CUSTOMER_TABS.map(([label], index) => index === CASE_FILE_TAB && !canDecideKyc ? null
          : <Tab key={label} value={index} label={label} id={`customer-tab-${index}`} aria-controls={`customer-panel-${index}`} />)}
      </Tabs>
      <Box role="tabpanel" id={`customer-panel-${tab}`} aria-labelledby={`customer-tab-${tab}`} sx={{ minWidth: 0 }}>
        <WorkspaceSectionHeader title={CUSTOMER_TABS[tab]?.[0] ?? 'Profile'} description={CUSTOMER_TABS[tab]?.[1] ?? ''}
          actions={tab === 1 && canDecideKyc ? <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <SetRiskRatingButton key={customerId} customerId={customerId} current={data.kyc.riskRating} />
            {/* Clearing "EDD required" (DEV-112); offered only when the status allows it. */}
            <ApproveKycButton key={`approve-${customerId}`} customerId={customerId} status={data.kyc.status} />
          </Stack>
            : tab === 2 && can('accounts.open') ? <Button size="small" variant="outlined" disabled={data.kyc.status !== 'APPROVED' && data.kyc.status !== 'ACTIVE'} onClick={() => { setOpenAccountIndustry(storedIndustry); setOpenAccountDialog(true); }}>Open account</Button>
              : tab === 4 && can('transactions.view') ? <Button size="small" variant="outlined" component={RouterLink} to={`/transactions?customerId=${customerId}`}>Open this customer’s transactions</Button> : undefined} />
        {tab === 0 ? (
          // Comments beside the profile, and wide enough to read: half the page
          // on a laptop, 8 of 12 columns on a wide screen, stacked below on a
          // narrow one. The old 340px rail was too narrow for a compliance note
          // (DEV-37); full width under the profile pushed them out of sight.
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) minmax(0,1fr)', xl: 'minmax(0,4fr) minmax(0,8fr)' },
            gap: 3,
            alignItems: 'start',
          }}>
            <Stack spacing={3} sx={{ minWidth: 0 }}>
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" component="h3">Identity</Typography>
                <Box component="dl" sx={{ mb: 0 }}>
                  {isBusiness ? <IdentityField label="Legal name">{data.business?.legalName ?? data.identity.displayName ?? 'Not recorded'}
                    {canDecideKyc && <CorrectLegalNameButton customerId={data.customerId} current={data.business?.legalName ?? data.identity.displayName ?? ''}
                      hasGuruPay={data.accounts.some((account) => account.provider === 'GURUPAY_VIBAN')}
                      storedIndustry={data.business?.gurupayIndustryType ?? null} />}</IdentityField>
                    : data.identity.displayName && can('pii.reveal') ? <IdentityField label="Full name">{data.identity.displayName}</IdentityField>
                      : renderPiiField('Full name', data.identity.maskedName, 'name')}
                  {renderPiiField('Email', data.identity.maskedEmail, 'email')}
                  {renderPiiField('Phone', data.identity.maskedPhone, 'phone')}
                  {isBusiness ? <>
                    <IdentityField label="Registration number">{data.business?.registrationNumber ?? 'Not recorded'}</IdentityField>
                    <IdentityField label="Country of registration">{data.business?.incorporationCountry ?? 'Not recorded'}</IdentityField>
                  </> : <>
                    {renderPiiField('Date of birth', data.identity.maskedDob, 'dob')}
                    <IdentityField label="Nationality">{data.identity.nationality}</IdentityField>
                  </>}
                </Box>
              </Paper>
              {/* Source of funds and the PEP declaration are KYC data: A / MLRO only (DEV-67 review). */}
              {isBusiness && canDecideKyc ? <DeclaredInformationPanel customerId={customerId} /> : null}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" component="h3">Available balances</Typography>
                {data.accounts.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No account open yet.</Typography> :
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    {Object.entries(data.accounts.reduce<Record<string, bigint>>((totals, account) => ({
                      ...totals, [account.currency]: (totals[account.currency] ?? 0n) + BigInt(account.availableBalanceCents || '0'),
                    }), {})).map(([currency, cents]) => <Box key={currency} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">{currency} · {data.accounts.filter((account) => account.currency === currency).length} accounts</Typography>
                      <Typography variant="h5" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{formatCents(cents.toString(), currency)}</Typography>
                    </Box>)}
                  </Stack>}
              </Paper>
            </Stack>
            {can('comments.view') ? <CommentsPanel subjectType="CUSTOMER" subjectId={customerId} customerId={customerId} canWrite={canComment} /> : null}
          </Box>
        ) : null}
        {tab === 1 ? <CustomerKycTab customer={data} canReports={canDecideKyc} canReadOnboarding={canReadOnboarding}
          retryBusy={isFetching} onRetry={() => { void refetch(); }} /> : null}
        {/* The company's own sanctions screening and its filed report (DEV-89). */}
        {tab === 1 && isBusiness ? <Box sx={{ mt: 3 }}><CompanyScreeningPanel customerId={customerId} canFile={canDecideKyc} /></Box> : null}
        {/* How the rating was reached, criterion by criterion (DEV-90). */}
        {tab === 1 && canDecideKyc ? <Box sx={{ mt: 3 }}><RiskScoringPanel customerId={customerId} /></Box> : null}

        {tab === 2 ? <Stack spacing={2}>
          {openAccountOutcome ? <Alert severity="info" onClose={() => setOpenAccountOutcome('')}>{openAccountOutcome}</Alert> : null}
          {data.kyc.status !== 'APPROVED' && data.kyc.status !== 'ACTIVE' ? <Typography variant="body2" color="text.secondary">An account can be opened once the customer is approved.</Typography> : null}
          {closeError ? <Alert severity="error" onClose={() => setCloseError(null)}>{closeError}</Alert> : null}
          <IbanqSubAccountStatus customerId={customerId} canRefresh={can('accounts.open')} />
          <CustomerAccountsPanel accounts={data.accounts} canStatements={can('statements')} onStatements={setStatementAccountId}
            closingId={closingId} closePending={closeAccountMutation.isPending}
            onCloseAccount={!can('customer.blocks') ? undefined : (account) => {
              const reason = window.prompt('Why is this account being closed? Kept on the record.', 'Opened automatically before a provider was chosen; not a real account.');
              if (reason === null || reason.trim().length < 4) return;
              setClosingId(account.accountId);
              closeAccountMutation.mutate({ accountId: account.accountId, reason: reason.trim() });
            }} />
          {can('statements') && statementAccount ? <AccountStatementsDialog key={`${customerId}:${statementAccount.accountId}`}
            customerId={customerId} customerName={data.identity.displayName ?? data.business?.legalName ?? data.identity.maskedName}
            account={statementAccount} onClose={() => setStatementAccountId(null)} /> : null}
        </Stack> : null}

          {tab === 3 ? (
            <Box sx={{ overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Card ID</TableCell>
                  <TableCell>Last 4</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.cards.length === 0 ? (
                  <EmptyRow colSpan={4} message="No cards have been issued to this customer." />
                ) : null}
                {data.cards.map((c) => (
                  <TableRow key={c.cardId}>
                    <TableCell>
                      <code>{c.cardId}</code>
                    </TableCell>
                    <TableCell>•••• {c.last4}</TableCell>
                    <TableCell>{c.cardType}</TableCell>
                    <TableCell>{c.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          ) : null}

          {tab === 4 ? (
            canSeeTransactions ? (
              <Stack spacing={2}>
                <CustomerPaymentsTab
                  key={customerId}
                  customerId={customerId}
                  canReveal={can('pii.reveal')}
                />
              </Stack>
            ) : (
              <Alert severity="info">
                Your role does not permit viewing transactions.
              </Alert>
            )
          ) : null}

          {tab === 5 ? (
            <Stack spacing={1}>
              {/* "Unknown" is the honest answer when the compliance database
                  could not be reached; reporting 0 would tell the officer there
                  is nothing outstanding. */}
              <Typography>
                <b>Open alerts:</b>{' '}
                {data.complianceFlags.openAlertsCount === null
                  ? 'Unknown — compliance data unavailable'
                  : String(data.complianceFlags.openAlertsCount)}
              </Typography>
              <Typography>
                <b>Sanctions screening:</b>{' '}
                {data.complianceFlags.sanctionsScreeningStatus}
                {data.complianceFlags.screenedAt !== null &&
                data.complianceFlags.screenedAt !== undefined
                  ? ` (${new Date(data.complianceFlags.screenedAt).toLocaleString(APP_LOCALE)})`
                  : ''}
              </Typography>
              {/* An unscreened customer must never look like a cleared one. */}
              {data.complianceFlags.sanctionsScreeningStatus === 'NOT_SCREENED' ? (
                <Alert severity="warning">
                  No sanctions screening result is recorded for this customer. Do not
                  treat this as a pass — screen before deciding.
                </Alert>
              ) : null}
              {data.complianceFlags.hasOpenSar === true ? (
                <Alert severity="warning">
                  A SAR case is open for this customer. No tipping-off — restricted
                  views apply.
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {tab === 6 ? (
            <Box sx={{ overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Granted at</TableCell>
                  <TableCell>Revoked at</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.consents.length === 0 ? (
                  <EmptyRow colSpan={4} message="No consents recorded for this customer." />
                ) : null}
                {data.consents.map((c) => (
                  <TableRow key={c.type}>
                    <TableCell>{c.type}</TableCell>
                    <TableCell>
                      <Chip
                        label={c.granted ? 'Granted' : 'Revoked'}
                        color={c.granted ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {c.grantedAt !== null ? new Date(c.grantedAt).toLocaleString(APP_LOCALE) : '—'}
                    </TableCell>
                    <TableCell>
                      {c.revokedAt !== null ? new Date(c.revokedAt).toLocaleString(APP_LOCALE) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          ) : null}
          {tab === 7 ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Parties to identify
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The provider has no company-level identity check, so each UBO and director
                verifies individually. Issue a link and send it to that person.
              </Typography>
              {/* Destructive party management is off by default — see the
                  removal dialog for what it actually does. */}
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Switch
                    size="small"
                    checked={manageParties}
                    onChange={(event) => setManageParties(event.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Edit parties (allows removing a person from the provider)
                  </Typography>
                }
              />
              {linkError !== null ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {linkError}
                </Alert>
              ) : null}
              <Table size="small" sx={{ mb: 4 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Party</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Screening</TableCell>
                    <TableCell>Identity verification</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(partiesData?.parties ?? []).length === 0 ? (
                    <EmptyRow
                      colSpan={5}
                      message="No parties are recorded at the provider. If this is a business
                        application, its submission never reached the provider — push it below."
                    />
                  ) : null}
                  {(partiesData?.parties ?? []).map((party) => {
                    const link = issuedLinks[party.providerId];
                    return (
                      <TableRow key={party.providerId}>
                        <TableCell>{party.fullName}</TableCell>
                        <TableCell>{party.role}</TableCell>
                        <TableCell>{party.screeningState || 'Not read back'}</TableCell>
                        <TableCell>
                          {/* Read back from the provider each time this loads. */}
                          {party.identityDecision === 'APPROVED' ? (
                            <Chip size="small" color="success" label="APPROVED" />
                          ) : party.identityDecision === 'REJECTED' ? (
                            <Chip size="small" color="error" label="REJECTED" />
                          ) : link !== undefined ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <code style={{ fontSize: 12 }}>{link}</code>
                              <Button
                                size="small"
                                onClick={() => void navigator.clipboard.writeText(link)}
                              >
                                Copy
                              </Button>
                            </Stack>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={issueLinkMutation.isPending}
                              onClick={() => issueLinkMutation.mutate(party.providerId)}
                            >
                              Issue link
                            </Button>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {manageParties ? (
                            <Button
                              size="small"
                              color="error"
                              disabled={removeBusyId === party.providerId}
                              onClick={() =>
                                setRemoveTarget({
                                  providerId: party.providerId,
                                  fullName: party.fullName,
                                  decision: party.identityDecision,
                                })
                              }
                            >
                              {removeBusyId === party.providerId ? 'Removing…' : 'Remove'}
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* The way back from a submission that never reached the provider.
                  Without this, a company sitting in the queue with no provider
                  profile could not be verified at all — there was nothing to
                  issue a link against and no server-side path to create it. */}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  disabled={pushState === 'busy'}
                  onClick={() => void pushToProvider()}
                >
                  {pushState === 'busy' ? 'Sending…' : 'Send application to provider'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Creates the company and one profile per director and owner at the provider
                  configured now. Safe to run again — it uses the stored application.
                </Typography>
              </Stack>
              {pushMessage !== '' ? (
                <Alert
                  severity={
                    pushState === 'error' ? 'error' : pushState === 'partial' ? 'warning' : 'success'
                  }
                  sx={{ mb: 3 }}
                >
                  {pushMessage}
                  {needsCompanyRelink ? (
                    <Stack spacing={1.25} sx={{ mt: 1.5, maxWidth: 560 }}>
                      <Typography variant="body2">
                        Open this company in Platforma365, copy the UUID from its URL, and attach
                        that existing record. Norvix verifies the ID before writing anything.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          label="Platforma365 company ID"
                          value={providerCompanyId}
                          onChange={(event) => setProviderCompanyId(event.target.value.trim())}
                          error={providerCompanyId !== '' && !PROVIDER_COMPANY_ID_RE.test(providerCompanyId)}
                          sx={{ flexGrow: 1 }}
                        />
                        <Button
                          variant="contained"
                          color="inherit"
                          disabled={!PROVIDER_COMPANY_ID_RE.test(providerCompanyId)}
                          onClick={() => void pushToProvider(providerCompanyId)}
                        >
                          Attach and continue
                        </Button>
                      </Stack>
                    </Stack>
                  ) : null}
                </Alert>
              ) : null}
              <DuplicatePersonAttachPanel
                customerId={customerId}
                refusals={partiesData?.refusals ?? []}
                onAttached={async () => {
                  setPushState('idle');
                  setPushMessage('');
                  await refetchParties();
                }}
              />
              {partiesFailed ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  The parties list could not be loaded, so this is not evidence that the
                  customer has none.
                </Alert>
              ) : null}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Provider records
              </Typography>
            <Box sx={{ overflowX: 'auto' }}><Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Screening</TableCell>
                  <TableCell>Provider record</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.providerReferences.length === 0 ? (
                  <EmptyRow
                    colSpan={5}
                    message="Nothing has been created at an identity provider for this customer."
                  />
                ) : null}
                {data.providerReferences.map((ref) => (
                  <TableRow key={`${ref.provider}-${ref.providerId}`}>
                    <TableCell>{ref.role}</TableCell>
                    <TableCell>{ref.displayName ?? '—'}</TableCell>
                    <TableCell>{ref.provider}</TableCell>
                    <TableCell>{ref.screeningState ?? 'Not read back'}</TableCell>
                    <TableCell>
                      {ref.providerUrl !== null ? (
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={ref.providerUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Open in provider
                        </Button>
                      ) : (
                        <code>{ref.providerId}</code>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
            </>
          ) : null}

          {tab === 8 ? (
            <CustomerFeesTab
              key={customerId}
              customerId={customerId}
            />
          ) : null}

          {tab === 9 ? <CustomerActivityTab customerId={customerId} /> : null}
          {tab === CASE_FILE_TAB && canDecideKyc ? (
            <CustomerCaseFileTab
              customerId={customerId}
              isBusiness={data.accountType === 'BUSINESS' || data.business !== null}
            />
          ) : null}
      </Box>

      <Dialog open={openAccountDialog} onClose={() => setOpenAccountDialog(false)}>
        <DialogTitle>Open an account for this customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Opens the account and activates it. Safe to run again — the same
            choices resolve to the same account rather than opening a second.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              select
              fullWidth
              label="Currency"
              value={openAccountCurrency}
              onChange={(event) => setOpenAccountCurrency(event.target.value)}
              helperText={
                optionsForChosenCurrency.length === 0
                  ? `Nothing can open a ${openAccountCurrency} account yet.`
                  : `${String(optionsForChosenCurrency.length)} ${
                      optionsForChosenCurrency.length === 1 ? 'provider' : 'providers'
                    } can open this.`
              }
            >
              {/* Only the offered currencies a switched-on provider can hold,
                  each with how many could; the others are named below with
                  the reason, so nobody learns our configuration by a refusal
                  after picking a provider and typing a reason. */}
              {currencyOptions.choosable.map(({ code, providers: count }) => {
                return (
                  <MenuItem key={code} value={code}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <span>{code}</span>
                      <Typography variant="caption" color="text.secondary">
                        {`${String(count)} available`}
                      </Typography>
                    </Stack>
                  </MenuItem>
                );
              })}
            </TextField>
            <TextField
              select
              fullWidth
              label="Account type"
              value={openAccountType}
              onChange={(event) => setOpenAccountType(event.target.value)}
            >
              {(providersQuery.data?.productTypes ?? ['CURRENT', 'SAVINGS']).map((type) => (
                <MenuItem key={type} value={type}>
                  {type === 'CURRENT' ? 'Current account' : 'Savings account'}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            select
            fullWidth
            label="Provider"
            value={openAccountProvider}
            onChange={(event) => setOpenAccountProvider(event.target.value)}
            sx={{ mb: 2 }}
            helperText={
              chosenProvider === undefined
                ? 'Which institution holds the account.'
                : chosenProvider.enabled
                  ? chosenProvider.description
                  : chosenProvider.disabledReason
            }
          >
            {providers.map((provider) => {
              // Unconnected providers stay visible but unselectable: hiding
              // them would leave an operator wondering whether we support the
              // partner at all, and the reason is the useful part. The same
              // applies to one that simply holds no account in this currency.
              const servesCurrency = provider.currencies.includes(openAccountCurrency);
              return (
                <MenuItem
                  key={provider.id}
                  value={provider.id}
                  disabled={!provider.enabled || !servesCurrency}
                >
                  {provider.label}
                  {!provider.enabled
                    ? ' — not available'
                    : servesCurrency
                      ? ''
                      : ` — no ${openAccountCurrency} account`}
                </MenuItem>
              );
            })}
          </TextField>
          {currencyOptions.choosable.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No provider can open an account in any offered currency. A partner holding a
              shared account offers only the currencies it has one for: add one in
              Settings → Treasury → Accounts, or switch a provider on.
            </Alert>
          ) : null}
          {currencyOptions.missing.length > 0 ? (
            <Alert severity="info" sx={{ mb: 2 }} aria-label="Currencies not available">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Offered, but no provider can open them now:</Typography>
              {currencyOptions.missing.map((m) => (
                <Typography key={m.code} variant="body2">{m.code}: {m.why}</Typography>
              ))}
            </Alert>
          ) : null}
          {chosenProvider?.currencyNote !== undefined ? (
            <Alert severity="info" sx={{ mb: 2 }}>{chosenProvider.currencyNote}</Alert>
          ) : null}
          {asksIndustry ? (
            <Box sx={{ mb: 2 }}>
              <GurupayIndustryTypeField value={openAccountIndustry} onChange={setOpenAccountIndustry}
                helperText={storedIndustry === null
                  ? "GuruPay keeps it on the company's vIBAN. Choose it from the company's actual activity on the KYB file: it is a classification."
                  : "On the client’s file already. Change it only if the company’s activity was classified wrongly."} />
            </Box>
          ) : null}
          {asksGics ? (
            <TextField
              fullWidth
              label="GICS sub-industry code"
              value={openAccountGics}
              onChange={(event) => setOpenAccountGics(event.target.value.replace(/[^0-9]/g, '').slice(0, 8))}
              error={openAccountGics !== '' && !gicsValid}
              helperText="IBANQ needs the company's GICS sub-industry code (8 digits, e.g. 45103010) to open its sub-account. Choose it from the company's actual activity on the KYB file; it is kept on the client's file and not asked again."
              inputProps={{ inputMode: 'numeric', 'aria-label': 'GICS sub-industry code' }}
              sx={{ mb: 2 }}
            />
          ) : null}
          {openAccountError !== '' ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {openAccountError}
            </Alert>
          ) : null}
          <TextField
            autoFocus
            fullWidth
            label="Reason (required)"
            value={openAccountReason}
            onChange={(event) => setOpenAccountReason(event.target.value)}
            helperText="Recorded in the audit log, e.g. “approved before automatic opening existed”."
            sx={{ minWidth: 380 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAccountDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              openAccountReason.trim().length < 4 ||
              openAccountBusy ||
              chosenProvider === undefined ||
              !chosenProvider.enabled ||
              (asksIndustry && openAccountIndustry === null) ||
              (asksGics && !gicsValid)
            }
            onClick={() => void openAccount(asksIndustry && openAccountIndustry !== null && openAccountIndustry !== storedIndustry
              ? openAccountIndustry : undefined, asksGics && gicsValid ? Number(openAccountGics.trim()) : undefined)}
          >
            {openAccountBusy ? 'Opening…' : 'Open account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* A typed reason, not a browser confirm: this destroys evidence, and the
          reason is written into the audit record. */}
      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove {removeTarget?.fullName} from the provider?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This unlinks them from the company and deletes their profile at the provider.
            {removeTarget?.decision === 'APPROVED'
              ? ' Their completed identity check is deleted with it and cannot be restored — they would have to verify again.'
              : ' Any verification in progress is lost.'}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Reason (required)"
            value={removeReason}
            onChange={(event) => setRemoveReason(event.target.value)}
            helperText="Recorded in the audit log, e.g. “duplicate profile created by a re-send”."
            sx={{ minWidth: 380 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={removeReason.trim().length < 4 || removeBusyId !== null}
            onClick={() => void confirmRemoval()}
          >
            {removeBusyId !== null ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quieten unused queryClient warning while leaving hook available for future invalidations */}
      {void queryClient}
    </Box>
  );
}
